import DOMPurify from "dompurify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, AlertCircle } from "lucide-react";
import { looksLikeHtml } from "@/lib/gmailMime";
import { stripHtml } from "@/lib/citationUtils";
import type { CommunicationInboxItem } from "@/types/communications";

interface MessageDetail extends CommunicationInboxItem {
  body?: string;
  bodyHtml?: string;
}

interface CommunicationMessageDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message?: MessageDetail | null;
  summary?: CommunicationInboxItem | null;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
}

function formatDate(date: string | undefined): string {
  if (!date) return "";
  try {
    return new Date(date).toLocaleString();
  } catch {
    return date;
  }
}

export function CommunicationMessageDetailDialog({
  open,
  onOpenChange,
  message,
  summary,
  isLoading = false,
  isError = false,
  errorMessage,
}: CommunicationMessageDetailDialogProps) {
  const display = message ?? summary;
  const subject = display?.subject || "Message";
  const from = message?.from ?? summary?.from;
  const to = message?.to ?? summary?.to;
  const date = message?.date ?? summary?.date;

  const bodyHtml = message?.bodyHtml;
  const plainBody =
    message?.body ||
    (message?.preview && !looksLikeHtml(message.preview) ? message.preview : undefined) ||
    (summary?.preview && !looksLikeHtml(summary.preview) ? stripHtml(summary.preview) : undefined);

  const richHtml =
    bodyHtml ||
    (message?.body && looksLikeHtml(message.body) ? message.body : undefined) ||
    (message?.preview && looksLikeHtml(message.preview) ? message.preview : undefined);

  const sanitizedHtml = richHtml ? DOMPurify.sanitize(richHtml) : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="pr-6">{subject}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1 text-sm text-left pt-1">
              {from && (
                <p>
                  <span className="text-muted-foreground">From:</span> {from}
                </p>
              )}
              {to && (
                <p>
                  <span className="text-muted-foreground">To:</span> {to}
                </p>
              )}
              {date && (
                <p>
                  <span className="text-muted-foreground">Date:</span> {formatDate(date)}
                </p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 pr-4 -mr-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading message...
            </div>
          ) : isError ? (
            <div className="space-y-3 py-4">
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{errorMessage || "Failed to load message details"}</span>
              </div>
              {plainBody && (
                <div className="text-sm whitespace-pre-wrap border-t pt-3 text-muted-foreground">
                  {plainBody}
                </div>
              )}
            </div>
          ) : sanitizedHtml ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none pb-2"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : plainBody ? (
            <div className="text-sm whitespace-pre-wrap pb-2">{plainBody}</div>
          ) : (
            <p className="text-sm text-muted-foreground py-4">No message content available.</p>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
